import type { ToastType } from '@trimble/types';
import type { AlertVariant } from '../alert/alert.utils';

const FADE_IN_DURATION_MS = 150;
export const FADE_OUT_DURATION_MS = 300;
export const MIN_DELAY_TIME_MS = 500;

// Styles are injected once into document.head rather than inline in the
// Portal JSX. Inline <style> tags inside a <Show>/<Portal> would be
// re-appended on every show/hide cycle and never cleaned up, leaking DOM
// nodes over the lifetime of the app.
//
// We use a DOM getElementById check instead of a module-level ref counter
// so the guard works correctly when multiple bundles coexist on the same
// page (e.g. two web-component instances each with their own module scope).
// The style element is intentionally never removed — it's a tiny static
// stylesheet that is harmless to keep for the page lifetime.
export const TOAST_STYLE_ID = 'solidjs-modus-ui-toast-styles';

export const TOAST_CONTAINER_CLASS = 'solidjs-modus-ui-toast';
export const TOAST_FADE_OUT_CLASS = 'solidjs-modus-ui-toast--fade-out';

export const TOAST_CSS = `
  @keyframes solidjs-modus-ui-toast-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes solidjs-modus-ui-toast-fade-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  .${TOAST_CONTAINER_CLASS} { animation: solidjs-modus-ui-toast-fade-in ${String(FADE_IN_DURATION_MS)}ms ease-out; }
  .${TOAST_CONTAINER_CLASS}.${TOAST_FADE_OUT_CLASS} { animation: solidjs-modus-ui-toast-fade-out ${String(FADE_OUT_DURATION_MS)}ms ease-in forwards; }
  /* Override both: allow wrapping and cap width so long messages
     wrap to a second line instead of stretching the toast off-screen. */
  .${TOAST_CONTAINER_CLASS} .modus-wc-toast {
    white-space: normal;
    max-width: min(480px, calc(100vw - 2rem));
    width:100%;
  }
`;

/**
 * Maps {@link ToastType} to the Modus `modus-wc-alert` colour variant.
 *
 * `error`, `warning`, `success`, and `info` map directly to their
 * corresponding Modus alert variant.
 *
 * `default` maps to `undefined` — no variant class is applied, which renders
 * as the gray/neutral base style of `modus-wc-alert`.
 *
 * `danger`, `secondary`, `dark`, and `primary` are legacy aliases retained for
 * backward-compatibility with existing `setToastMessage` call sites in the app.
 * Prefer the direct variants for new code.
 */
export const TOAST_TYPE_TO_ALERT_VARIANT: Record<
  ToastType,
  AlertVariant | undefined
> = {
  error: 'error',
  warning: 'warning',
  success: 'success',
  info: 'info',
  default: undefined,
  danger: 'error',
  secondary: 'info',
  dark: 'info',
  primary: 'info',
};
